import { Chip, LinearProgress, Typography } from '@mui/material'
import { KcTable } from '../common/kc-table'
import { CustomNoRowsOverlay } from '../common/kc-table/components'
import dayjs from 'dayjs'
import {
  formatDateTime,
  formatTimeRemaining,
} from '../../pages/HL7historyLogs/utils'
import Layout from '../../layouts/common/Layout'
import AccessRestrictedMessage from './AccessRestricted'

const columns = [
  {
    field: 'orderNumber',
    headerName: 'Order Number',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => {
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
          }}
        >
          {params?.value || '-'}
        </Typography>
      )
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => {
      return (
        <Chip
          size="small"
          label={params?.value === 0 ? 'Failed' : 'Success'}
          color={params?.value === 0 ? 'error' : 'success'}
        />
      )
    },
  },
  {
    field: 'retryCount',
    headerName: 'Retry Count',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => {
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
          }}
        >
          {params?.value || 0}
        </Typography>
      )
    },
  },
  {
    field: 'recordedAt',
    headerName: 'Recorded At',
    minWidth: 200,
    flex: 1,
    type: 'dateTime',
    renderCell: (params) =>
      params?.value ? formatDateTime(params.value) : 'N/A',
    sortComparator: (v1, v2) => {
      return dayjs(v1).isAfter(dayjs(v2)) ? 1 : -1
    },
  },
  {
    field: 'sentAt',
    headerName: 'Sent At',
    minWidth: 200,
    flex: 1,
    type: 'dateTime',
    renderCell: (params) =>
      params?.value ? formatDateTime(params.value) : 'N/A',
    sortComparator: (v1, v2) => {
      return dayjs(v1).isAfter(dayjs(v2)) ? 1 : -1
    },
  },
  {
    field: 'processingTime',
    headerName: 'Processing Time',
    minWidth: 100,
    flex: 1,
    renderCell: ({ row }) => {
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
          }}
        >
          {formatTimeRemaining(row.recordedAt, row.sentAt) || 'N/A'}
        </Typography>
      )
    },
  },
]

function DicomAuditLogTable({ hasHl7LogAccess = false, dicomSenderLogs = {} }) {
  const {
    dicomeSenderLogsData,
    error,
    loading,
    pageNo,
    pageSize,
    setPage,
    setPageSize,
  } = dicomSenderLogs

  const totalCount = dicomeSenderLogsData?.totalCount || 0

  const renderError = () => {
    if (error) {
      return (
        <Layout.CenterWrapper>
          <Typography variant="h6" color="error">
            {error?.status === 401
              ? 'Unauthorized access. Please log in again.'
              : 'An error occurred while fetching HL7 logs. Please try again later.'}
          </Typography>
        </Layout.CenterWrapper>
      )
    }
    return null
  }

  if (error && error?.status !== 421) {
    return renderError()
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 200,
      }}
    >
      <KcTable
        rows={dicomeSenderLogsData?.listOfLogs || []}
        autoHeight
        maxHeight="100%"
        columns={columns}
        getRowId={(row) => row.id}
        rowCount={totalCount || 0}
        loading={loading}
        sortingOrder={['desc', 'asc']}
        initialState={{
          sorting: {
            sortModel: [{ field: 'recordedAt', sort: 'desc' }],
          },
        }}
        pageNo={pageNo}
        pageSize={pageSize}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        pagination
        paginationMode="server"
        removeToolbar
        removePadding
        components={{
          LoadingOverlay: LinearProgress,
          NoResultsOverlay: () => (
            <CustomNoRowsOverlay text="No Dicom Audit Logs Available" />
          ),
          NoRowsOverlay: () => (
            <CustomNoRowsOverlay
              text={
                !hasHl7LogAccess ? (
                  <AccessRestrictedMessage />
                ) : (
                  'No Dicom Audit Logs Found'
                )
              }
            />
          ),
        }}
      />
    </div>
  )
}

export default DicomAuditLogTable
